import React, { useState, useEffect, useRef } from 'react';
import TripManagementHeader from './TripManagementHeader';
import TripTable from './TripTable';
import { Plus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { CommonButton } from '@/components/ui/common-button';
import tripService from '@/services/tripService';
import Loader, { FullScreenLoader, TableBlurLoader } from '@/components/ui/loader';
import AddTripDialog from './addtrip/AddTripDialog';
import ViewTripDetailsDialog from './viewdetails/ViewTripDetailsDialog';
import EditTripDialog from './edittrip/EditTripDialog';
import OtpDetailsDialog from './viewdetails/OtpDetailsDialog';
import ChangeDriverDialog from './changedriver/ChangeDriverDialog';
import CreateTicketDialog from './createticket/CreateTicketDialog';
import { usePagination } from '@/hooks/usePagination';
import { useAuth } from '@/contexts/AuthContext';
import { CommonSearchInput } from '@/components/ui/common-search-input';
import { Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { mapTripToTableRow } from '@/lib/tripRouteUtils';
import { getRakePointNameMap } from '@/lib/rakePointNameCache';

const TripManagementPage = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [isAddTripDialogOpen, setIsAddTripDialogOpen] = useState(false);
  const [isViewTripDialogOpen, setIsViewTripDialogOpen] = useState(false);
  const [isEditTripDialogOpen, setIsEditTripDialogOpen] = useState(false);
  const [isOtpDialogOpen, setIsOtpDialogOpen] = useState(false);
  const [isChangeDriverDialogOpen, setIsChangeDriverDialogOpen] = useState(false);
  const [isCreateTicketDialogOpen, setIsCreateTicketDialogOpen] = useState(false);
  const [selectedTripDetails, setSelectedTripDetails] = useState(null);
  const [tripToEdit, setTripToEdit] = useState(null);
  const [tripToChangeDriver, setTripToChangeDriver] = useState(null);
  const [tripForTicket, setTripForTicket] = useState(null);
  const [tripDetailsLoading, setTripDetailsLoading] = useState(false);
  const [totalTrips, setTotalTrips] = useState(0);

  const { currentPage, pageSize, setPage } = usePagination({ defaultPage: 1, defaultPageSize: 10 });
  const { userProfile } = useAuth();

  const { toast } = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  // const searchTimeoutRef = useRef(null);

  const isDistributor = userProfile?.role === 'distributor';
  const isTransporter = userProfile?.role === 'transporter';
  const isSuperAdmin = ['ipl_super_admin', 'super_admin', 'superadmin'].includes(
    (userProfile?.role || userProfile?.user_role || '').toLowerCase()
  );
  const canManageTrips =
    userProfile?.role === 'ipl_super_admin' ||
    userProfile?.role === 'port_admin' ||
    userProfile?.role === 'transporter' ||
    userProfile?.role === 'distributor';

  useEffect(() => {
    const search = searchParams.get('search') || '';
    setSearchQuery(search);
    fetchTrips(search);
  }, [currentPage, pageSize, searchParams.get('search')]);


  useEffect(() => {
    const timer = setTimeout(() => {
      const currentSearchParam = searchParams.get('search') || '';
      if (searchQuery !== currentSearchParam) {
        handleSearch(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getStartOtp = (trip) => {
    if (trip.start_otp) {
      return trip.start_otp;
    }
    if (trip.otp_info && trip.otp_info.start_trip_otp) {
      return trip.otp_info.start_trip_otp;
    }
    return 'N/A';
  };

  // const getDeliveryOtp = (trip) => {
  //   if (trip.delivery_otp) {
  //     return trip.delivery_otp;
  //   }
  //   if (trip.otp_info && trip.otp_info.end_trip_otp) {
  //     return trip.otp_info.end_trip_otp;
  //   }
  //   return 'N/A';
  // };

  const parseSearchQuery = (query) => {
    const searchParams = {
      tripNumber: '',
      createdDate: '',
      vehicleNumber: '',
      driverMobileNumber: ''
    };

    if (!query) return searchParams;

    const trimmedQuery = query.trim();

    if (trimmedQuery.includes(' ')) {
      const parts = trimmedQuery.split(' ');
      searchParams.tripNumber = parts[0] || '';
      searchParams.createdDate = parts[1] || '';
      searchParams.vehicleNumber = parts[2] || '';
      searchParams.driverMobileNumber = parts[3] || '';
    } else {
      searchParams.tripNumber = trimmedQuery;
    }

    return searchParams;
  };

  const fetchTrips = async (query = searchQuery) => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize;

      const parsedParams = parseSearchQuery(query);

      const response = await tripService.getTrips(
        skip,
        pageSize,
        parsedParams.tripNumber,
        parsedParams.createdDate,
        parsedParams.vehicleNumber,
        parsedParams.driverMobileNumber
      );

      const rakePointNameById = await getRakePointNameMap();

      let tripsData, totalCount;

      if (response && typeof response === 'object' && Array.isArray(response.data)) {
        tripsData = response.data;
        totalCount = response.total || response.count || tripsData.length;
      }
      else if (Array.isArray(response)) {
        tripsData = response;
        totalCount = response.length < pageSize ? skip + response.length : skip + response.length + 1;
      }
      else if (response && typeof response === 'object' && Array.isArray(response.trips)) {
        tripsData = response.trips;
        totalCount = response.total || response.count || tripsData.length;
      }
      else {
        tripsData = [];
        totalCount = 0;
      }

      const transformedTrips = tripsData.map((trip) => mapTripToTableRow(trip, { rakePointNameById }));

      setTrips(transformedTrips);
      setTotalTrips(totalCount);
      setError(null);
    } catch (error) {
      setError('Failed to load trips');
      toast({
        title: "Error",
        description: "Failed to load trips. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const newParams = new URLSearchParams(searchParams);

    if (query) {
      newParams.set('search', query);
    } else {
      newParams.delete('search');
    }

    newParams.set('page', '1');
    setPage(1);

    setSearchParams(newParams);
  };

  const handleSearchInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchQuery(inputValue);
  };

  const handleSearchButtonClick = () => {
    handleSearch(searchQuery);
  };

  const fetchTripDetails = async (tripId) => {
    try {
      setTripDetailsLoading(true);
      const response = await tripService.getTripById(tripId);
      setSelectedTripDetails(response);
      setIsViewTripDialogOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load trip details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setTripDetailsLoading(false);
    }
  };

  const fetchTripOtpDetails = async (tripId) => {
    try {
      setTripDetailsLoading(true);

      const response = await tripService.getTripOtps(tripId);
      setSelectedTripDetails(response);
      setIsOtpDialogOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load OTP details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setTripDetailsLoading(false);
    }
  };

  const fetchTripForEdit = async (tripId) => {
    try {
      setTripDetailsLoading(true);
      const response = await tripService.getTripById(tripId);
      setTripToEdit(response);
      setIsEditTripDialogOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load trip details for editing. Please try again.",
        variant: "destructive",
      });
    } finally {
      setTripDetailsLoading(false);
    }
  };

  const handleCreateTrip = () => {
    setIsAddTripDialogOpen(true);
  };

  const handleCreateTripSubmit = async (newTrip) => {
    try {
      const response = await tripService.createTrip(newTrip);
      const rakePointNameById = await getRakePointNameMap();

      const transformedTrip = mapTripToTableRow(response, { rakePointNameById });

      setTrips(prevTrips => [transformedTrip, ...prevTrips]);
      // setIsAddTripDialogOpen(false);

      // toast({
      //   title: "Trip Created",
      //   description: `Trip ${response.trip_number} has been successfully created.`,
      // });

      return response;
    } catch (error) {
      toast({
        title: "Error Creating Trip",
        description: error.message || "Failed to create trip. Please check the form and try again.",
        variant: "destructive",
      });
      throw error;
    }
  };


  const handleUpdateTrip = async (updatedTripData) => {
    try {
      const response = await tripService.updateTrip(tripToEdit.id, updatedTripData);
      const rakePointNameById = await getRakePointNameMap();

      const updatedTrips = trips.map(trip => {
        if (trip.id === tripToEdit.id) {
          return {
            ...trip,
            ...mapTripToTableRow(response, { rakePointNameById }),
          };
        }
        return trip;
      });

      setTrips(updatedTrips);
      setIsEditTripDialogOpen(false);
      setTripToEdit(null);

      toast({
        title: "Trip Updated",
        description: `Trip ${response.trip_number} has been successfully updated.`,
      });

      return response;
    } catch (error) {
      toast({
        title: "Error Updating Trip",
        description: error.message || "Failed to update trip. Please check the form and try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDeleteTrip = async (tripId) => {
    try {
      const tripToDelete = trips.find(trip => trip.id === tripId);

      if (tripToDelete && (
        tripToDelete.status === 'ACTIVE' ||
        tripToDelete.status === 'COMPLETED' ||
        tripToDelete.status === 'AUTO_COMPLETE' ||
        tripToDelete.status === 'AUTO_COMPLETED'
      )) {
        toast({
          title: "Cannot Delete Trip",
          description: `Trip ${tripToDelete.tripNumber} is currently ${tripToDelete.status.toLowerCase().replace('_', ' ')} and cannot be deleted.`,
          variant: "destructive",
        });
        return;
      }

      await tripService.deleteTrip(tripId);

      setTrips(prevTrips => prevTrips.filter(trip => trip.id !== tripId));

      toast({
        title: "Trip Deleted",
        description: `Trip has been successfully deleted.`,
      });
    } catch (error) {
      toast({
        title: "Error Deleting Trip",
        description: error.message || "Failed to delete trip. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleGenerateStartOtp = (tripId) => {
    toast({
      title: "Generate Start OTP",
      description: `Generating Start OTP for trip ${tripId}.`,
    });
  };

  const handleViewTripDetails = (tripId) => {
    fetchTripDetails(tripId);
  };

  const handleViewOtpDetails = (tripId) => {
    fetchTripOtpDetails(tripId);
  };

  const handleEditTrip = (tripId) => {
    fetchTripForEdit(tripId);
  };

  const handleChangeDriver = (trip) => {
    setTripToChangeDriver(trip);
    setIsChangeDriverDialogOpen(true);
  };

  const handleCreateTicket = (trip) => {
    setTripForTicket(trip);
    setIsCreateTicketDialogOpen(true);
  };

  const handleCreateTicketSubmit = async (tripId, ticketData) => {
    try {
      await tripService.raiseTicket(tripId, ticketData);
      toast({
        title: "Ticket Raised",
        description: `Correction ticket for trip has been successfully raised.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to raise ticket. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDriverChanged = async (updatedTrip) => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize;

      const searchParams = parseSearchQuery(searchQuery);

      const response = await tripService.getTrips(
        skip,
        pageSize,
        searchParams.tripNumber,
        searchParams.createdDate,
        searchParams.vehicleNumber,
        searchParams.driverMobileNumber
      );

      let tripsData, totalCount;

      if (response && typeof response === 'object' && Array.isArray(response.data)) {
        tripsData = response.data;
        totalCount = response.total || response.count || tripsData.length;
      }
      else if (Array.isArray(response)) {
        tripsData = response;
        totalCount = response.length < pageSize ? skip + response.length : skip + response.length + 1;
      }
      else if (response && typeof response === 'object' && Array.isArray(response.trips)) {
        tripsData = response.trips;
        totalCount = response.total || response.count || tripsData.length;
      }
      else {
        tripsData = [];
        totalCount = 0;
      }

      const rakePointNameById = await getRakePointNameMap();
      const transformedTrips = tripsData.map((trip) => mapTripToTableRow(trip, { includeDriverPhone: true, rakePointNameById }));

      setTrips(transformedTrips);
      setTotalTrips(totalCount);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh trip list. Please reload the page.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  if (error) {
    return <div className="flex justify-center items-center h-64 text-red-500">{error}</div>
  }

  const totalPages = Math.ceil(totalTrips / pageSize);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Trip Management</h1>
          <p className="text-xs text-muted-foreground">Track, schedule, and review status logs of all cargo shipments.</p>
        </div>
        {canManageTrips && (
          <div className="flex items-center space-x-2">
            <CommonButton onClick={handleCreateTrip} variant="default" icon={Plus}>
              Create Trip
            </CommonButton>
          </div>
        )}
      </div>

      <TripManagementHeader trips={trips} />

      <div className="mb-4 w-full mt-4">
        <div className="md:w-1/3">
          <CommonSearchInput
            placeholder="Search by trip number, created date..."
            value={searchQuery}
            onChange={handleSearchInputChange}
            onSearch={handleSearch}
            maxLength={50}
          />
        </div>
      </div>

      <TableBlurLoader
        isLoading={loading}
        className="mt-4"
      >
        <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform" style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border py-4 rounded-[16px] bg-background">
            {trips.length > 0 ? (
              <TripTable
                trips={trips}
                onGenerateStartOtp={handleGenerateStartOtp}
                onViewDetails={handleViewTripDetails}
                onViewOtpDetails={handleViewOtpDetails}
                onEditTrip={canManageTrips ? handleEditTrip : null}
                onDeleteTrip={canManageTrips ? handleDeleteTrip : null}
                onChangeDriver={isTransporter ? handleChangeDriver : null}
                onCreateTicket={(userProfile?.role?.toLowerCase() === 'distributor' || userProfile?.role?.toLowerCase() === 'port_admin' || userProfile?.role?.toLowerCase() === 'transporter') ? handleCreateTicket : null}
                currentPage={currentPage}
                totalPages={totalPages}
                totalTrips={totalTrips}
                pageSize={pageSize}
                onPageChange={setPage}
                isDistributor={isDistributor}
                isTransporter={isTransporter}
                isSuperAdmin={isSuperAdmin}
              />
            ) : (
              <div className="flex justify-center items-center h-64 text-muted-foreground">
                {loading ? "" : "No data available"}
              </div>
            )}
          </div>
        </div>
      </TableBlurLoader>


      {canManageTrips && (
        <AddTripDialog
          open={isAddTripDialogOpen}
          onOpenChange={setIsAddTripDialogOpen}
          onCreateTrip={handleCreateTripSubmit}
        />
      )}

      <ViewTripDetailsDialog
        open={isViewTripDialogOpen}
        onOpenChange={setIsViewTripDialogOpen}
        tripDetails={selectedTripDetails}
        loading={tripDetailsLoading}
      />

      <OtpDetailsDialog
        open={isOtpDialogOpen}
        onOpenChange={setIsOtpDialogOpen}
        tripDetails={selectedTripDetails}
      />

      {isTransporter && (
        <ChangeDriverDialog
          open={isChangeDriverDialogOpen}
          onOpenChange={setIsChangeDriverDialogOpen}
          tripData={tripToChangeDriver}
          onChangeDriver={handleDriverChanged}
        />
      )}

      {canManageTrips && (
        <EditTripDialog
          open={isEditTripDialogOpen}
          onOpenChange={setIsEditTripDialogOpen}
          tripData={tripToEdit}
          onUpdateTrip={handleUpdateTrip}
        />
      )}

      <CreateTicketDialog
        open={isCreateTicketDialogOpen}
        onOpenChange={setIsCreateTicketDialogOpen}
        trip={tripForTicket}
      />
    </div>
  );
};

export default TripManagementPage;